import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import {
  Home, BookOpen, CandlestickChart, Sparkles, Wallet, Calculator,
  GraduationCap, Search, MessageSquare, Trophy, Menu, X, LogOut, TrendingUp, Youtube, Crown, CreditCard,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/course", label: "Course", icon: BookOpen },
  { to: "/charts", label: "Charts", icon: CandlestickChart },
  { to: "/exercises", label: "Trend Exercises", icon: Sparkles },
  { to: "/simulator", label: "Simulator", icon: Wallet },
  { to: "/calculator", label: "Risk Calculator", icon: Calculator },
  { to: "/quizzes", label: "Quizzes", icon: GraduationCap },
  { to: "/glossary", label: "Dictionary", icon: Search },
  { to: "/videos", label: "Video Lessons", icon: Youtube },
  { to: "/tutor", label: "AI Tutor", icon: MessageSquare },
  { to: "/progress", label: "My Progress", icon: Trophy },
  { to: "/subscription", label: "Subscription", icon: CreditCard },
];

function SidebarContent({ onNav }) {
  return (
    <div className="flex flex-col h-full">
      <NavLink to="/" onClick={onNav} data-testid="brand-logo" className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800 shrink-0">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-extrabold text-[15px] text-white">Trading Academy</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Learn · Practice · Risk-Free</div>
        </div>
      </NavLink>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNav}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="text-[10px] text-slate-600 font-mono px-2 leading-relaxed">Virtual funds only. No real trading.</div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, progress, logout } = useAuth();
  const navigate = useNavigate();

  const upgrade = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); }
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#0B1120] border-r border-slate-800 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0B1120] border-r border-slate-800">
            <SidebarContent onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-6 bg-[#090D16]/85 backdrop-blur-md border-b border-slate-800">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
            Paper Funds
            <span className="text-emerald-400 font-semibold">$100,000.00</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {user?.plan === "pro" && (
              <span data-testid="pro-badge" className="hidden sm:flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-lg px-2.5 py-1.5">
                <Crown className="w-3.5 h-3.5" /> Pro
              </span>
            )}
            {user?.plan === "free" && (
              <button data-testid="header-upgrade-btn" onClick={upgrade} className="flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg px-3 py-1.5 transition-colors">
                <Sparkles className="w-3.5 h-3.5" /> Upgrade
              </button>
            )}
            {progress && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700" data-testid="header-level">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold text-slate-200">{progress.level}</span>
                <span className="text-xs font-mono text-blue-400">{progress.xp} XP</span>
              </div>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center text-xs font-bold text-white" title={user.name}>
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="text-slate-400 hover:text-red-400 transition-colors">
                  <LogOut className="w-[18px] h-[18px]" />
                </button>
              </div>
            ) : (
              <button data-testid="header-login-btn" onClick={() => navigate("/login")} className="text-sm font-medium text-blue-400 hover:text-blue-300">Sign in</button>
            )}
          </div>
        </header>

        <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      </div>
    </div>
  );
}
